"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { cn } from "@/components/utils";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui";
import { Label } from "@/components/ui";
import { Checkbox } from "@/components/ui";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui";
import { loginUser } from "@/api/auth";
import { LoginRequest } from "@/common/interface/auth";
import { useAuth } from "@/components/contexts";
import Link from "next/link";
import { toast } from "sonner";
// import Script from 'next/script'

const loginSchema = z.object({
  schoolCode: z
    .string()
    .min(5, "학교 코드를 입력해주세요.")
    .length(5, "학교 코드는 5자리입니다.")
    .regex(/^\d{5}$/, "학교 코드는 숫자 5자리입니다."),
  name: z.string().min(1, "성명을 입력해주세요.").trim(),
  password: z
    .string()
    .min(1, "비밀번호를 입력해주세요.")
    .min(4, "비밀번호는 4자리 이상이어야 합니다."),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const router = useRouter();
  const { login } = useAuth();
  const [autoLogin, setAutoLogin] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      schoolCode: "",
      name: "",
      password: "",
    },
  });

  const schoolCodeValue = watch("schoolCode");

  const onSubmit = async (data: LoginFormData) => {
    try {
      const formData: LoginRequest = {
        schoolCode: data.schoolCode.length === 5
          ? `${data.schoolCode[0]}-${data.schoolCode.slice(1)}`
          : data.schoolCode,
        name: data.name.trim(),
        password: data.password,
      };

      const response = await loginUser(formData);

      login(response.user);

      if (autoLogin) {
        localStorage.setItem("autoLogin", "true");
      } else {
        localStorage.removeItem("autoLogin");
      }

      router.push("/console");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "로그인에 실패했습니다.";
      toast.error(errorMessage);
    }
  };

  return (
    <form
      className={cn("flex flex-col gap-6", className)}
      onSubmit={handleSubmit(onSubmit)}
      {...props}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">로그인</h1>
      </div>
      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="school_code">학교 코드</Label>
          <div className="flex justify-center w-full">
            <InputOTP
              maxLength={5}
              value={schoolCodeValue}
              onChange={(value) => setValue("schoolCode", value, { shouldValidate: true })}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          {errors.schoolCode && (
            <p className="text-red-500 text-sm">{errors.schoolCode.message}</p>
          )}
          <p className="text-muted-foreground text-sm text-balance">
            운영자에게 받은 학교 코드 5자리를 입력해주세요.
          </p>
        </div>
        <div className="grid gap-3">
          <Label htmlFor="name">성명</Label>
          <Input
            id="name"
            type="text"
            placeholder="홍길동"
            {...register("name")}
            required
          />
          {errors.name && (
            <p className="text-red-500 text-sm">{errors.name.message}</p>
          )}
          <p className="text-muted-foreground text-sm text-balance">
            동명이인이 있다면 운영자에게 받은 성명을 입력해주세요.
          </p>
        </div>
        <div className="grid gap-3">
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            type="password"
            placeholder="• • • • • • • •"
            {...register("password")}
            required
          />
          {errors.password && (
            <p className="text-red-500 text-sm">{errors.password.message}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="auto_login"
            checked={autoLogin}
            onCheckedChange={(checked) => setAutoLogin(checked as boolean)}
          />
          <Label htmlFor="auto_login">
            자동 로그인(반드시 본인 기기에서만 사용하세요.)
          </Label>
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "로그인 중..." : "로그인"}
        </Button>
      </div>
      <div className="flex justify-center gap-2 text-sm text-center">
        <Link href="/signup" className="underline underline-offset-4">
          회원가입
        </Link>
        <a
          onClick={() => alert("학교 운영자에게 문의바랍니다.")}
          className="underline underline-offset-4 cursor-pointer"
        >
          계정 찾기
        </a>
      </div>
    </form>
  );
}
